import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Send, ArrowLeft } from 'lucide-react';
import { Ticket as TicketType } from '../../types';
import toast from 'react-hot-toast';

export const SupportTickets: React.FC = () => {
    const { clientTickets, createTicket, addTicketReply, updateTicketStatus, refreshData } = useData();
    const { user } = useAuth();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [priority, setPriority] = useState<'bajo' | 'medio' | 'alto'>('bajo');
    const [replyMessage, setReplyMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
    const [isLoadingTicket, setIsLoadingTicket] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!subject || !message || !user) return;

        setIsSubmitting(true);
        const toastId = toast.loading('Enviando ticket...');
        try {
            await createTicket({ clientId: user.id, subject, message, priority });
            setSubject('');
            setMessage('');
            setPriority('bajo');
            toast.success('Ticket enviado con éxito.', { id: toastId });
        } catch (error) {
            toast.error('Error al enviar el ticket.', { id: toastId });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSelectTicket = async (ticket: TicketType) => {
        if (selectedTicket?.id === ticket.id) {
            setSelectedTicket(null);
            return;
        }
        setIsLoadingTicket(true);
        try {
            const detailedTicket = await api.getTicketDetails(ticket.id);
            setSelectedTicket(detailedTicket);
        } catch (error) {
            toast.error('Error al cargar los detalles del ticket.');
        } finally {
            setIsLoadingTicket(false);
        }
    };

    const handleReplySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!replyMessage.trim() || !selectedTicket || !user) return;

        const originalTicketId = selectedTicket.id;
        const toastId = toast.loading('Enviando respuesta...');
        try {
            await addTicketReply(originalTicketId, {
                authorId: user.id,
                authorRole: 'client',
                message: replyMessage,
            });
            setReplyMessage('');
            const updatedTicket = await api.getTicketDetails(originalTicketId);
            setSelectedTicket(updatedTicket);
            toast.success('Respuesta enviada.', { id: toastId });
        } catch (error) {
            toast.error('No se pudo enviar la respuesta.', { id: toastId });
        }
    };

    const handleStatusChange = async (ticketId: string, newStatus: 'abierto' | 'cerrado') => {
        const toastId = toast.loading('Actualizando estado...');
        try {
            await updateTicketStatus(ticketId, newStatus);
            if (selectedTicket?.id === ticketId) {
                const updatedTicket = await api.getTicketDetails(ticketId);
                setSelectedTicket(updatedTicket);
            }
            await refreshData();
            toast.success('Estado del ticket actualizado.', { id: toastId });
        } catch (error) {
            toast.error('Error al actualizar el estado.', { id: toastId });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'abierto': return 'bg-blue-100 text-blue-800';
            case 'en_progreso': return 'bg-yellow-100 text-yellow-800';
            case 'cerrado': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (selectedTicket) {
        return (
            <div>
                <button onClick={() => { setSelectedTicket(null); }} className="flex items-center text-sm text-blue-600 hover:underline mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Volver a mis tickets
                </button>
                <div className="bg-white rounded-xl border flex flex-col h-[calc(100vh-14rem)]">
                    <div className="p-4 border-b flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold">{selectedTicket.subject}</h2>
                            <p className="text-sm text-gray-500">Creado: {new Date(selectedTicket.createdAt).toLocaleString('es-ES')}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(selectedTicket.status)}`}>{selectedTicket.status.replace('_', ' ')}</span>
                    </div>
                    <div className="flex-1 p-6 space-y-4 overflow-y-auto bg-gray-50">
                        {selectedTicket.replies?.map((reply: any) => (
                            <div key={reply.id} className={`flex ${reply.authorRole === 'client' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-xl p-3 rounded-lg shadow-sm ${reply.authorRole === 'client' ? 'bg-blue-500 text-white' : 'bg-white text-gray-800 border'}`}>
                                    <p className="text-sm font-semibold mb-1">{reply.authorRole === 'admin' ? 'Soporte' : 'Tú'}</p>
                                    <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                                    <p className="text-xs opacity-75 mt-1 text-right">{new Date(reply.createdAt).toLocaleTimeString('es-ES', {hour: '2-digit', minute:'2-digit'})}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    {selectedTicket.status !== 'cerrado' ? (
                        <div className="p-4 border-t bg-white">
                            <form onSubmit={handleReplySubmit} className="flex space-x-3">
                                <input type="text" value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder="Escribe tu respuesta..." className="flex-1 p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"><Send className="w-5 h-5" /></button>
                            </form>
                            <button onClick={() => handleStatusChange(selectedTicket.id, 'cerrado')} className="text-xs text-gray-500 hover:underline mt-2">Marcar como cerrado</button>
                        </div>
                    ) : (
                        <div className="p-4 border-t bg-gray-50 text-center">
                            <p className="text-sm text-gray-600 mb-2">Este ticket está cerrado.</p>
                            <button onClick={() => handleStatusChange(selectedTicket.id, 'abierto')} className="text-sm text-blue-600 hover:underline">¿Reabrir ticket?</button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Abrir Nuevo Ticket</h3>
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl border space-y-4">
                    <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">Asunto</label>
                        <input type="text" id="subject" value={subject} onChange={e => setSubject(e.target.value)} className="mt-1 w-full p-2 border rounded-md" required />
                    </div>
                    <div>
                        <label htmlFor="priority" className="block text-sm font-medium text-gray-700">Prioridad</label>
                        <select id="priority" value={priority} onChange={e => setPriority(e.target.value as any)} className="mt-1 w-full p-2 border rounded-md bg-white">
                            <option value="bajo">Bajo</option>
                            <option value="medio">Medio</option>
                            <option value="alto">Alto</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">Mensaje</label>
                        <textarea id="message" value={message} onChange={e => setMessage(e.target.value)} rows={5} className="mt-1 w-full p-2 border rounded-md" required />
                    </div>
                    <button type="submit" disabled={isSubmitting} className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50">
                        {isSubmitting ? 'Enviando...' : 'Enviar Ticket'}
                    </button>
                </form>
            </div>
            <div className="lg:col-span-2">
                 <h3 className="text-xl font-bold text-gray-900 mb-4">Mis Tickets</h3>
                 <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <table className="min-w-full divide-y">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asunto</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Última Actualización</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y">
                            {clientTickets.map(ticket => (
                                <tr key={ticket.id} onClick={() => handleSelectTicket(ticket)} className="hover:bg-gray-50 cursor-pointer">
                                    <td className="px-6 py-4 font-medium">{ticket.subject}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(ticket.status)}`}>
                                            {ticket.status.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">{new Date(ticket.updatedAt).toLocaleString('es-ES')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </div>
            </div>
        </div>
    );
};
